import type { ComponentType } from 'react';

function dispatchPreloadError(error: Error): void {
  const event: VitePreloadErrorEvent = Object.assign(
    new Event('vite:preloadError', { cancelable: true }),
    { payload: error },
  );
  window.dispatchEvent(event);
}

/**
 * Routes both chunk-load failure modes through vite:preloadError so the
 * cache-busted reload-once recovery runs instead of a raw crash:
 *   1. Safari/iOS disk-cache corruption — import() resolves with an empty/invalid
 *      module (net::ERR_CACHE_READ_FAILURE); React reads `.default` off undefined
 *      and throws deep inside its internals.
 *   2. Network rejection — import() rejects (CDN timeout, 5xx); without the catch
 *      below, the rejection propagates without ever firing vite:preloadError.
 */
export async function loadWithFallbackDetection(
  loader: () => Promise<{ default: ComponentType }>,
): Promise<{ default: ComponentType }> {
  const loadedModule = await loader().catch((err: unknown) => {
    dispatchPreloadError(err instanceof Error ? err : new Error(String(err)));
    throw err;
  });

  if (!loadedModule?.default) {
    const error = new Error(
      'Failed to fetch dynamically imported module: chunk resolved without a default export',
    );
    dispatchPreloadError(error);
    throw error;
  }

  return loadedModule;
}
